import { UserManager, WebStorageStateStore, Log, SigninPopupArgs, UserManagerSettings } from "oidc-client-ts";
import subscriptions from "@services/subscriptions/instance";
import { ApplicationPaths, ApplicationName } from "./ApiAuthorizationConstants";
import IAuthorizeService, { ExtUser } from "./AuthorizeServiceInterface";

// export interface ExtUser extends User {
//   profile: User["profile"] & {
//     user_id: string;
//     puzzel: string;
//     first_name: string;
//     last_name: string;
//   };
// }

export class AuthorizeService implements IAuthorizeService {
  _callbacks: Array<{ callback: Function; subscription: number }> = [];
  _nextSubscriptionId: number = 0;
  _user: ExtUser | null | undefined | void = null;
  _isAuthenticated: boolean = false;
  _managerInitializing: boolean = false;
  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled: boolean = true;
  _accessToken: string | undefined = undefined;
  userManager: UserManager | undefined = undefined;

  constructor() {
    this._callbacks = [];
    this._nextSubscriptionId = 0;
    this._isAuthenticated = false;
    // this.oidcClient = null;
    this.userManager = undefined;
    this._accessToken = undefined;
    subscriptions.subscribe("SignIn", "AuthorizeService", (caller: string) => {
      this.signIn();
    });
    this.messageSink();
  }

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.

  messageSink() {
    // eslint-disable-next-line no-restricted-globals
    self.onmessage = async (event: MessageEvent) => {
      if (event.data.origin === "AxiosAPIService") {
        if (event.data.type === "SignIn") {
          await this.signIn();
        }
      }
    };
  }

  async isAuthenticated() {
    const user = await this.getUser();
    // this._isAuthenticated = !!user;
    return !!user;
  }

  async getUser() {
    if (this._user && this._user.profile) {
      return this._user;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager?.getUser() as ExtUser;
    user && this.userLoaded(user);
    user && (this._accessToken = user.access_token);
    return user;
  }

  async getAccessToken() {
    const user = await this.getUser();
    if (!user) {
      return;
    }
    user && (this._accessToken = user.access_token);
    return user && user.access_token;
  }

  public accessToken = async () => {
    if (this._accessToken) {
      return this._accessToken;
    }
    return await this.getAccessToken();
  };

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(state?: { returnUrl: any } | undefined) {
    await this.ensureUserManagerInitialized();
    // try {
    //   await this.userManager?.signinSilent(this.createArguments());
    //   return this.success(state);
    // } catch (silentError) {
    //   // User might not be authenticated, fallback to popup authentication

    //   try {
    //     if (this._popUpDisabled) {
    //       throw new Error(
    //         "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
    //       );
    //     }

    //     await this.userManager?.signinPopup(this.createArguments());
    //     return this.success(state);
    //   } catch (popUpError: any) {
    //     if (popUpError.message === "Popup window closed") {
    //       // The user explicitly cancelled the login action by closing an opened popup.
    //       return this.error("The user closed the window.");
    //     } else if (!this._popUpDisabled) {
    //     }

    //     // PopUps might be blocked by the user, fallback to redirect
    try {
      // check again if user is not authorized
      const user = await this.userManager?.getUser() as ExtUser;
      if (user) {
        // notify subscribers
        this.userLoaded(user);
        return this.success(state);
      }
      await this.userManager?.signinRedirect({ extraQueryParams: { prompt: "login" } });
      return this.redirect();
    } catch (redirectError) {
      return this.error(redirectError);
      // }
      // }
    }
  }

  async isAboutToExpire() {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager?.getUser();
    if (!user) {
      return true;
    }

    const expiresIn = user.expires_in || 0;
    return expiresIn < 60; // If the token is less than a minute away, warn the user.
  }

  async completeSignIn(url: string) {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager?.signinCallback(url);
      return this.success(user && user.state);
    } catch (error) {
      return this.error("There was an error signing in.");
    }
  }

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state: any) {
    await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error(
          "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
        );
      }

      await this.userManager?.signoutPopup(this.createArguments());
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      try {
        await this.userManager?.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        return this.error(redirectSignOutError);
      }
    }
  }

  async completeSignOut(url: string) {
    await this.ensureUserManagerInitialized();
    try {
      await this.userManager?.signoutCallback(url);
      this.updateState(null);
      return this.success({ status: "success" });
    } catch (error) {
      return this.error(error);
    }
  }

  updateState(user: ExtUser | null | undefined | void) {
    if (
      !user ||
      this._user?.session_state !== user?.session_state ||
      this._user?.access_token !== user?.access_token ||
      this._user?.id_token !== user?.id_token ||
      this._user?.refresh_token !== user?.refresh_token ||
      this._user?.expires_at !== user?.expires_at ||
      this._user?.scope !== user?.scope ||
      JSON.stringify(this._user?.profile) !== JSON.stringify(user?.profile)
    ) {
      this._user = user;
      this._isAuthenticated = !!this._user;
      this.notifySubscribers();
    }
  }

  subscribe(callback: (_isAuthenticated: boolean, user: ExtUser) => void) {
    this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex = this._callbacks
      .map((element: { subscription: number }, index: any) =>
        element.subscription === subscriptionId ? { found: true, index } : { found: false }
      )
      .filter((element: { found: boolean }) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback(this._isAuthenticated, this._user);
    }
  }

  createArguments(state?: any): SigninPopupArgs {
    return { state };
  }

  error(state: any) {
    return { status: AuthenticationResultStatus.Fail, state };
  }

  success(state: any) {
    return { status: AuthenticationResultStatus.Success, state };
  }

  redirect() {
    return { status: AuthenticationResultStatus.Redirect };
  }

  // publishInformation;

  userLoaded = (user: ExtUser) => {
    if (user) {
      this.updateState(user as ExtUser);
      return
    }
    this.userManager?.getUser().then(
      (user) => {
        this.updateState(user as ExtUser);
      }
    ).catch((error) => {
      console.error(error);
    });
  };

  async ensureUserManagerInitialized() {
    if (this.userManager !== undefined) {
      return;
    }

    this._managerInitializing = true;

    let response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
    if (!response.ok) {
      throw new Error(`Could not load settings for '${ApplicationName}'`);
    }

    const settings = (await response.json()) as UserManagerSettings;

    const post_logout_redirect_uri = settings.post_logout_redirect_uri?.includes(",") ? settings.post_logout_redirect_uri?.split(",").find((uri) => {
      return uri.includes(window.location.origin);
    })
      : settings.post_logout_redirect_uri;

    if (!post_logout_redirect_uri) {
      throw new Error(`Could not find a valid post_logout_redirect_uri for '${ApplicationName}'`);
    }

    settings.post_logout_redirect_uri = post_logout_redirect_uri;

    const redirect_uri = settings.redirect_uri?.includes(",") ? settings.redirect_uri?.split(",").find((uri) => {
      return uri.includes(window.location.origin);
    })
      : settings.redirect_uri;

    if (!redirect_uri) {
      throw new Error(`Could not find a valid redirect_uri for '${ApplicationName}'`);
    }

    settings.redirect_uri = redirect_uri;

    settings.userStore = new WebStorageStateStore({
      prefix: ApplicationName,
      store: window.localStorage,
    });


    this.userManager = new UserManager(settings);

    ///////////////////////////////
    // events
    ///////////////////////////////
    this.userManager.events.addAccessTokenExpiring(() => {
      // subscriptions.publish("accessTokenExpiring", "");
      // console.log("accessTokenExpiring", user);
      // this.signIn();
    });

    this.userManager.events.addUserSessionChanged(() => {
      subscriptions.publish("userSessionChanged", "user session changed");
      // console.log("userSessionChanged");
    });

    this.userManager.events.addAccessTokenExpired(() => {
      // subscriptions.publish("accessTokenExpired", user);
      this.signIn();
      // console.log("accessTokenExpired", user);
    });

    this.userManager.events.addSilentRenewError(() => {
      // console.log("silentRenewError", e);
    });

    this.userManager.events.addUserUnloaded(() => {
      // console.log("user unloaded event");
      this.updateState(undefined);
    });

    this.userManager.events.addUserLoaded((user) => {
      // console.log("user loaded event", user);
      this.userLoaded(user as ExtUser);
    });

    Log.setLevel(Log.INFO);
    Log.setLogger(console);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager?.removeUser();
      this.updateState(undefined);
    });

    this._managerInitializing = false;
  }

  static get instance() {
    return authService;
  }
}

export const authService = new AuthorizeService();

//declare on window
(window as any).authService = authService;

export const AuthenticationResultStatus = {
  Redirect: "redirect",
  Success: "success",
  Fail: "fail",
};
